import { JsonPipe, NgTemplateOutlet } from "@angular/common";
import { AfterViewInit, Component, computed, effect, inject, input, OnDestroy, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { BehaviorSubject, combineLatest, map, of, Subject, switchMap, tap, zip } from "rxjs";
import introJs from 'intro.js';
import { SchoolClass } from "src/app/core/models/school-class.model";
import { AssignDiagnosticService } from "src/app/core/services/assign-diagnostic.service";
import { EnrollmentService, TeacherSubscriptionCount } from "src/app/core/services/enrollment.service";
import { LoadingService } from "src/app/core/services/loading.service";
import { SessionStorageService } from "src/app/core/services/session-storage.service";
import { UsersService } from "src/app/core/services/users.service";
import { toMap } from "src/app/core/utils/data";
import { SortColumn, sortUpdate } from "src/app/core/utils/table";
import { RemainingSubscriptionsComponent } from "../remaining/remaining-subscriptions.component";
import { Session } from "inspector";
import { ManagementUser } from "src/app/core/models/management-user.model";
import { SchoolTeacherFilterComponent } from "src/app/shared/components/school-teacher-filter/school-teacher-filter.component";
import { UserRoles } from "src/app/core/models/user.model";
import { SchoolTeacherFilterOptions } from "src/app/core/models/filters";
import { ActivatedRoute, ActivatedRouteSnapshot } from "@angular/router";
import { District } from "src/app/core/models/district.model";
import { School } from "src/app/core/models/school.model";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { MainContentComponent } from "src/app/shared/components/main-content/main-content.component";
import { SubscriptionSummaryComponent } from "../summary/subscription-summary.component";
import { manageTeacherSubscriptionsIntroSteps } from './manage-teacher-subscriptions.intro-steps';

@Component({
  selector: 'wf-manage-teacher-subscriptions',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    FormsModule,
    SubscriptionSummaryComponent,
    SchoolTeacherFilterComponent,
    MainContentComponent,
  ],
  styleUrl: 'manage-teacher-subscriptions.css',
  styles: `
    :host {
      display: block;
      height: 100%;
    }
  `,
  template: `
    @let summary = subscriptionsExisting()?.summary;
    @let showDiagnosticSubscriptions = summary && !summary.diagnostic.unlimited && (summary.diagnostic.unassigned || summary.diagnostic.teacher || summary.diagnostic.student);
    @let showSystemSubscriptions = summary && !summary.system.unlimited && (summary.system.unassigned || summary.system.teacher || summary.system.student);

    <button class="tourPageBtn mr-3" id="help-tour-btn" (click)="startTour()" title="Take a tour">
      <img src="/assets/images/tutorialOverlayBttn.svg" />
    </button>
    <wf-main-content categoryName="Enroll" [categoryIcon]="enrollIcon" toolName="Teacher Subscriptions">
      @if (userData()?.userRole === UserRoles.CUSTOMER_SERVICE) {
        <div class="tool-scope">
          <wf-school-teacher-filter
            [padding]="false"
            [districts]="districts ?? []"
            [showTeacher]="false"
            [chainLoad]="true"
            (filterChangeEvent)="districtSchool.set($event)"/>
        </div>
      }
      <div class="top-container">
        <div class="remaining-subscriptions-container">
          @if (summary) {
            @if (showDiagnosticSubscriptions) {
              <wf-subscription-summary id="diagnostic-subscriptions" subscriptionsType="diagnostic" [summary]="summary.diagnostic" [editTotal]="subscriptionsEditTotals().diagnostic"/>
            }
            @if (showSystemSubscriptions) {
              <wf-subscription-summary id="system-subscriptions" subscriptionsType="system" [summary]="summary.system" [editTotal]="subscriptionsEditTotals().system"/>
            }
          }
        </div>
        @let diagnosticInvalid = showDiagnosticSubscriptions && summary && (summary.diagnostic.unassigned < subscriptionsEditTotals().diagnostic || summary.diagnostic.teacher < -subscriptionsEditTotals().diagnostic);
        @let systemInvalid = showSystemSubscriptions && summary && (summary.system.unassigned < subscriptionsEditTotals().system || summary.system.teacher < -subscriptionsEditTotals().system);
        <div class='save-button-container'>
          <button
            class="btn save-button"
            (click)="save()"
            [disabled]="(!subscriptionsEdited().system && !subscriptionsEdited().diagnostic) || diagnosticInvalid || systemInvalid"
          >
            Save
          </button>
        </div>
        <!-- @if (diagnosticInvalid) {
          <span role="alert" class="too-many-selected-warning">You have selected {{ diagnosticRemaining * -1 }} too many Diagnostic subscriptions</span>
        }
        @if (systemInvalid) {
          <span role="alert" class="too-many-selected-warning">You have selected {{ systemRemaining * -1 }} too many System subscriptions</span>
        } -->
        <div class="scrollable-container">
          <table class="report-table">
            <colgroup>
              @if (showDiagnosticSubscriptions) {
                <col style="width: 240px"/>
              }
              @if (showSystemSubscriptions) {
                <col style="width: 240px"/>
              }
              <col/>
            </colgroup>
            <thead>
              <ng-template #sortableTableHeader let-column="column">
                <th class="sortable-report-table-header">
                  <button
                    (click)="updateSort(column)"
                    [attr.aria-sort]="sortColumn().column === column ? (sortColumn().reverse ? 'desending' : 'ascending') : undefined"
                  >
                    {{ column }}
                    <div
                      [class]=" { 'arrow-up' : sortColumn().reverse, 'arrow-down': !sortColumn().reverse }"
                      [style.visibility]="(sortColumn().column !== column) ? 'hidden' : 'visible'"
                      class="print-hide"
                    ></div>
                  </button>
                </th>
              </ng-template>
              <tr>
                @if(showDiagnosticSubscriptions) {
                  <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.DIAGNOSTIC }"/>
                }
                @if(showSystemSubscriptions) {
                  <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.SYSTEM }"/>
                }
                <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.TEACHER_NAME }"/>
              </tr>
            </thead>
            <tbody>
              @for(teacher of teachersSorted(); track teacher.userID) {
                @let edit = subscriptionsEdit()[teacher.userID];
                <tr>
                  @if (showDiagnosticSubscriptions) {
                    <td>
                      <div class="subscription-count">
                        @if (edit) {
                          <input
                            type="number"
                            class="subscription-count-input"
                            [(ngModel)]="edit.diagnosticLicenseCount"
                            (change)="updateEdit(edit.teacherID, 'diagnosticLicenseCount')"
                          />
                          <div>
                            @let difference = $any(subscriptionsEditDifferences()[teacher.userID])?.diagnostic ?? 0;
                            @if (difference) {
                              ({{ (difference > 0) ? '+' : ''  }}{{ difference }})
                            }
                          </div>
                        }
                      </div>
                    </td>
                  }
                  @if (showSystemSubscriptions) {
                    <td>
                      @if (edit) {
                        <div class="subscription-count">
                            <input
                              type="number"
                              min="0"
                              class="subscription-count-input"
                              [(ngModel)]="edit.interventionLicenseCount"
                              (change)="updateEdit(edit.teacherID, 'interventionLicenseCount')"
                            />
                            <div>
                              @let difference = $any(subscriptionsEditDifferences()[teacher.userID])?.system ?? 0;
                              @if (difference) {
                                ({{ (difference > 0) ? '+' : ''  }}{{ difference }})
                              }
                            </div>
                        </div>
                      }
                    </td>
                  }
                  <th scope="row"> {{ teacher.lastName }}, {{teacher.firstName }} </th>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </wf-main-content>
  `
})
export class ManageTeacherSubscriptionsComponent implements AfterViewInit, OnDestroy {
  readonly grades = input<string[]>([]);
  readonly classes = input<SchoolClass[]>([]);

  readonly loadingService = inject(LoadingService);
  readonly enrollmentService = inject(EnrollmentService);
  readonly sessionStorageService = inject(SessionStorageService);
  readonly assignDiagnosticService = inject(AssignDiagnosticService);
  readonly usersService = inject(UsersService);
  readonly activatedRoute = inject(ActivatedRoute);

  readonly userData = inject(SessionStorageService).userData;

  readonly enrollIcon = faUserPlus;
  readonly UserRoles = UserRoles;

  readonly COLUMNS = {
    DIAGNOSTIC: 'Diagnostic',
    SYSTEM: 'System',
    TEACHER_NAME: 'Teacher Name',
  } as const;
  readonly sortColumn = signal<SortColumn>({
    column: this.COLUMNS.TEACHER_NAME,
    reverse: false,
  });

  readonly districts = this.activatedRoute.snapshot.data['filterData'].districts.filter((district: District) => district.name !== 'Clever District') ;
  readonly schools = this.activatedRoute.snapshot.data['filterData'].schools.filter((school: School) => school.name !== 'Clever School') ;

  readonly districtSchool = signal<SchoolTeacherFilterOptions | undefined>(undefined);
  readonly teachers = toSignal(
    combineLatest([
      toObservable(this.userData),
      toObservable(this.districtSchool),
    ]).pipe(
      switchMap(([user, ds]) =>
        {
          if (!user) {
            return of([])
          }
          return this.usersService
            .getSchoolUsers(ds?.school?.schoolID)
            .pipe(
              map(
                managementUsers => {
                  // if you are teacher school admin, you should be able to assign subs to yourself (as teacher role)
                  if (user.userRole === UserRoles.TEACHER_SCHOOL_ADMIN && user.effectiveUserRole === UserRoles.SCHOOL_ADMIN) {
                    managementUsers = [user, ...managementUsers];
                  }
                  return managementUsers;
                }
              )
            )
        }
      ),
      map(schoolUsers => schoolUsers.filter(u => u.userRole === UserRoles.TEACHER || u.userRole === UserRoles.TEACHER_SCHOOL_ADMIN)),
    )
  );
  readonly teachersSorted = computed(() => {
    const { column, reverse } = this.sortColumn();
    const existing = this.subscriptionsExisting();

    const teacherCmp = (t1: ManagementUser, t2: ManagementUser) => {
      const nameComp = t1.lastName.localeCompare(t2.lastName) || t1.firstName.localeCompare(t2.firstName);

      if (!column || !existing) {
        return nameComp;
      }
      let comp = nameComp;
      switch (column) {
        case this.COLUMNS.DIAGNOSTIC:
          comp = (existing.teachers[t1.userID]?.diagnosticLicenseCount ?? 0) - (existing.teachers[t2.userID]?.diagnosticLicenseCount ?? 0);
          break;
        case this.COLUMNS.SYSTEM:
          comp = (existing.teachers[t1.userID]?.interventionLicenseCount ?? 0) - (existing.teachers[t2.userID]?.interventionLicenseCount ?? 0);
          break;
      }
      if (comp === 0 && column !== this.COLUMNS.TEACHER_NAME) {
        // On tie of non-name columns, fall back to ascending name
        // This ignores reverse. Reverse should only affect the top level sort, not the tiebreaker.
        return nameComp;
      }

      return reverse ? (comp * -1) : comp
    };

    return [...(this.teachers() ?? [])].sort(teacherCmp);
  });

  // TODO: switch editSubscriptions to linkedSignal when they are available
  readonly subscriptionsEdit = signal<{ [teacherID: number]: TeacherSubscriptionCount }>({});
  readonly subscriptionsExistingRefresh$ = new BehaviorSubject<void>(undefined);
  readonly subscriptionsExisting = toSignal(
    combineLatest([
      toObservable(this.userData),
      toObservable(this.districtSchool),
      this.subscriptionsExistingRefresh$
    ]).pipe(
      tap(() => this.loadingService.start()),
      switchMap(([user, districtSchool]) => {
        const schoolID = districtSchool?.school?.schoolID || user?.schoolID;
        if (!user || !schoolID) {
          return of([]);
        }

        return zip([
          this.assignDiagnosticService.getSchoolSubscriptionSummary(schoolID),
          this.enrollmentService.getSchoolTeacherSubscriptionAvailability(schoolID)
            .pipe(map(teachers => {
              // we want to treat count -2 as 0 for purposes of difference calcs
              // ignore count -1 because they shouldn't even see the edit column if they have site-license
              teachers = teachers.map(t => ({
                ...t,
                diagnosticLicenseCount: Math.max(t.diagnosticLicenseCount, 0),
                interventionLicenseCount: Math.max(t.interventionLicenseCount, 0),
              }))
              return toMap(teachers, "teacherID")
            }))
        ]);
      }),
      map(([summary, teachers]) => ({ summary, teachers })),
      tap(() => this.loadingService.finish()),
      // TODO: Error handling
    )
  );

  // TODO: Replace with linkedSignal when they are available
  readonly subscriptionsExistingEffect = effect(() => {
    const teachers = this.teachers();
    const subs = this.subscriptionsExisting()?.teachers ?? {};
    const edit: typeof subs = {};
    for (const teacher of (teachers ?? [])) {
      const existing = subs[teacher.userID];
      edit[teacher.userID] = {
        teacherID: teacher.userID,
        diagnosticLicenseCount: existing?.diagnosticLicenseCount ?? 0,
        interventionLicenseCount: existing?.interventionLicenseCount ?? 0,
      }
    }
    this.subscriptionsEdit.set(edit);
  });

  readonly subscriptionsEditDifferences = computed(() => {
    const existingSubs = this.subscriptionsExisting()?.teachers ?? {};
    const edits = this.subscriptionsEdit();
    const differences: { [existingID: number]: { [subType in "diagnostic" | "system"]: number } } = {};

    for (const edit of Object.values(edits)) {
      const existing = existingSubs[edit.teacherID];
      // We don't have to care about licenseCount === -1 because they shouldn't even use this page if they have a site license for that type of sub
      if (edit) {
        differences[edit.teacherID] = {
          diagnostic: edit.diagnosticLicenseCount - (existing?.diagnosticLicenseCount ?? 0),
          system: edit.interventionLicenseCount - (existing?.interventionLicenseCount ?? 0),
        };
      }
    }
    return differences;
  });

  readonly subscriptionsEdited = computed(() => {
    const differences = this.subscriptionsEditDifferences();
    // Sum all edit differences into a single +/- number for that subtype
    return {
      diagnostic: Object.values(differences).some(d => d.diagnostic),
      system: Object.values(differences).some(d => d.system),
    }
  });

  readonly subscriptionsEditTotals = computed(() => {
    const differences = this.subscriptionsEditDifferences();
    // Sum all edit differences into a single +/- number for that subtype
    return {
      diagnostic: Object.values(differences).reduce((a, d) => a + d.diagnostic, 0),
      system: Object.values(differences).reduce((a, d) => a + d.system, 0),
    }
  });

  introTour: any;

  ngAfterViewInit(): void {
    this.setupIntroTour();
  }

  private filterIntroStepsByExistingElements(steps: any[]): any[] {
    return steps.filter(step => {
      if (!step.element) {
        return true;
      }

      try {
        const element = document.querySelector(step.element);
        return element !== null;
      } catch (error) {
        console.warn(`Invalid selector in intro step: ${step.element}`, error);
        return false;
      }
    });
  }

  setupIntroTour() {
    this.introTour = introJs();
    this.introTour.setOptions({
      steps: manageTeacherSubscriptionsIntroSteps,
      nextLabel: 'Next →',
      prevLabel: '← Back',
      doneLabel: 'Done',
      exitOnOverlayClick: false,
      showStepNumbers: true,
      disableInteraction: false,
      scrollToElement: true,
      showBullets: true,
      showSkip: false,
    });
  }

  startTour() {
    const filteredSteps = this.filterIntroStepsByExistingElements(manageTeacherSubscriptionsIntroSteps);
    this.introTour.setOptions({ steps: filteredSteps });
    this.introTour.start();
  }

  ngOnDestroy() {
    this.loadingService.finish();
  }

  updateSort(clickedColumn: string) {
    this.sortColumn.update(sortUpdate(clickedColumn));
  }

  updateEdit(teacherID: number, field: "diagnosticLicenseCount" | "interventionLicenseCount") {
    this.subscriptionsEdit.update(e => {
      const newEdits = { ...e, };
      const edited = newEdits[teacherID]?.[field];
      if (edited && edited < 0) {
        newEdits[teacherID][field] = 0;
      }
      return newEdits;
     });
  }

  save() {
    const user = this.sessionStorageService.userData();
    const summary = this.subscriptionsExisting()?.summary;
    const editTotals = this.subscriptionsEditTotals();

    if (
      !user
      || !summary
      || (editTotals.diagnostic > 0 && summary.diagnostic.unassigned < editTotals.diagnostic) // not enough unassigned to cover edits
      || (editTotals.diagnostic < 0 && summary.diagnostic.teacher < -editTotals.diagnostic ) // not enough teachers to unassign
      || (editTotals.system > 0 && summary.system.unassigned < editTotals.system) // not enough unassigned to cover edits
      || (editTotals.system < 0 && summary.system.teacher < -editTotals.system ) // not enough teachers to unassign
    ) {
      return;
    }

    const differences = this.subscriptionsEditDifferences()
    const diagnostic: { [teacherID: number]: number } = {};
    const system: { [teacherID: number]: number } = {};
    for (const [teacherID, difference] of Object.entries(differences)) {
      // if difference is not 0 (+ or -), then include them
      if (difference.diagnostic) {
        diagnostic[parseInt(teacherID)] = difference.diagnostic;
      }
      if (difference.system) {
        system[parseInt(teacherID)] = difference.system;
      }
    }

    this.loadingService.start();
    const schoolID = user.schoolID || this.districtSchool()?.school?.schoolID || 0;
    this.assignDiagnosticService.updateTeacherGrantedSubscriptions(schoolID, { diagnostic, system }).subscribe({
      next: () => {
        this.subscriptionsExistingRefresh$.next()
        this.loadingService.finish();
      },
      error: () => {
        this.subscriptionsExistingRefresh$.next()
        this.loadingService.finish();
      }
     })
  }
}
